import React, { FC } from 'react';
import { useLocation } from 'react-router-dom';

import { SearchQueryBasicFragment } from '../../generated/graphql';
import { extractTitle } from '../../hooks/elasticSearch/useEsSmartViewQuery/helpers';

import { getIsKanbanPage } from '../../routes/helpers/pages';
import { getFilterQuickUrl } from '../../routes/urls/searchParams/quickFilters';
import { generateRandomInt } from '../../utils/number';
import SkeletonListColumns from '../Loading/SkeletonListColumns';

import QuickFilterLink from './QuickFilterLink';
import { getIsQuickFilterActive, getKanbanQuickFilters } from './helpers';
import * as Styled from './styled';

type Props = {
  isLoading: boolean;
  quickFilters: SearchQueryBasicFragment[];
};

/**
 * Quick Filters component that renders links for ES
 *
 * @param props              Props passed to the component
 * @param props.isLoading    Whether the data is being fetched
 * @param props.quickFilters The quick filters to render
 * @returns                  Topic filters links component
 */
const QuickFilters: FC<Props> = ({ isLoading, quickFilters }) => {
  const location = useLocation();

  if (isLoading) {
    const numberOfLines = generateRandomInt(2, 5);

    return (
      <Styled.Wrapper>
        <SkeletonListColumns numberOfLines={numberOfLines} />
      </Styled.Wrapper>
    );
  }

  const isKanbanPage = getIsKanbanPage();

  if (isKanbanPage) {
    const kanbanQuickFilters = getKanbanQuickFilters(location);

    return (
      <Styled.Wrapper>
        <Styled.Ul>
          {kanbanQuickFilters.map(props => (
            <Styled.Li key={props.url}>
              <QuickFilterLink {...props} />
            </Styled.Li>
          ))}
        </Styled.Ul>
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper>
      <Styled.Ul>
        {quickFilters.map(({ id, title }) => {
          const isActive = getIsQuickFilterActive(location, id);

          /**
           * Create url based on if the filter is selected or not
           */
          const url = getFilterQuickUrl(location, isActive, id);
          return (
            <Styled.Li key={id}>
              <QuickFilterLink
                isActive={isActive}
                label={extractTitle(title)}
                url={url}
              />
            </Styled.Li>
          );
        })}
      </Styled.Ul>
    </Styled.Wrapper>
  );
};

export default QuickFilters;
